<script>
  import { onMount, getContext } from "svelte";

  import { Modal } from "svelte-utilities";
  import FileIcon from "@local/assets/icons/file.svg";
  import WinBidProIcon from "@local/assets/icons/win-bid-pro.svg";
  import GSIcon from "@local/assets/icons/glazier-studio.svg";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import { downloadJobBundle } from "#src/extensions/download-job-bundle";
  import { WBPVersion, GSVersion } from "#src/stores/ui.js";
  import ConfirmGsUpdate from "#src/lib/ConfirmGsUpdate.svelte";
  import eb from "#src/extensions/event-bus.js";
  import { profile } from "#src/stores/auth.js";
  import { alerts } from "#src/stores/alerts.js";

  export let title = "Download Files";
  export let group;
  export let types;
  export let items;
  export let collections;
  export let name;
  export let locations = [];
  export let tab = null;
  export const open = () => openModal;

  const edgeTreatments = getContext("edgeTreatments");

  let modal;
  let downloading = false;
  let includeData = false;
  let includeWBP = false;
  let includeCAD = false;
  let includeGS = false;
  let selectedOnly = false;
  let itemsCount = 0;
  let openingsCount = 0;
  let typesCount = 0;

  $: disabled =
    (!includeData && !includeWBP && !includeGS && !includeCAD) ||
    (includeGS && !$profile?.confirmed_gs_update_on);
  $: recount(selectedOnly);

  async function openModal() {
    count();
    modal.open();
  }

  function recount(so) {
    count();
  }

  function count() {
    if (selectedOnly) {
      itemsCount = locations.filter((loc) => loc.record_type === "item").length;
      openingsCount = locations.filter((loc) => loc.record_type === "collection").length;
      typesCount = locations.filter((loc) => loc.record_type === "type").length;
    } else {
      itemsCount = items.length;
      openingsCount = collections.length;
      typesCount = types.length;
    }
  }

  async function downloadBundle() {
    downloading = true;

    try {
      await downloadJobBundle(
        $group,
        types,
        { edgeTreatments: $edgeTreatments },
        {
          productQuote: $group?.project_type === "product",
          filename: `${name}`,
          includeData,
          includeWBP,
          includeCAD,
          includeGS,
          itemsOnly: tab === "items",
          collectionsOnly: tab === "openings",
          selectedLocations: selectedOnly ? locations.map((loc) => loc.id) : null,
          wbpVersion: $WBPVersion,
          gsVersion: $GSVersion,
        },
        $profile,
      );
      modal.close();
      downloading = false;
    } catch (error) {
      alerts.add({ type: "error", message: "Failed to download files." });
      console.log(error);
      modal.close();
      downloading = false;
    }
  }

  function showOpeningsDownloads() {
    if (!locations.length || !selectedOnly) {
      return tab === "openings" || (!tab && collections.length);
    } else {
      return locations.some((loc) => loc.record_type === "collection");
    }
  }

  onMount(() => {
    eb.on("open-list-download", openModal);

    return () => {
      eb.unsubscribe("open-list-download", openModal);
    };
  });
</script>

<Modal
  bind:this={modal}
  width="30rem"
  closeable
  on:confirm={downloadBundle}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Download", type: "confirm", style: "primary", closeOnClick: false, disabled },
  ]}>
  <div slot="title">{title}</div>
  <div slot="content" class="space-y-4 text-xs">
    {#key selectedOnly}
      {#if downloading}
        <div class="flex gap-2 items-center">
          <div>Downloading...</div>
          <div class="relative w-8 h-8">
            <div class="absolute">
              <div class="animate-spin">
                <SpinnerIcon />
              </div>
            </div>
          </div>
        </div>
      {:else}
        {@const showOpenings = showOpeningsDownloads()}
        {#if locations.length}
          <div class="space-y-2">
            <div class="flex gap-4 items-center">
              <label class="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="download-scope"
                  value={false}
                  bind:group={selectedOnly}
                  on:change={count} />
                Complete Job
              </label>
            </div>
            <div>
              <label class="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="download-scope"
                  value={true}
                  bind:group={selectedOnly}
                  on:change={count} />
                Selected Markers Only
              </label>
            </div>
          </div>
        {/if}
        <div class="flex gap-8 justify-center items-center">
          <div class="flex flex-col items-center w-16 gap-2">
            <FileIcon />
            <div>Excel</div>
            <input type="checkbox" bind:checked={includeData} />
            <div class="text-gray-500 italic">
              {#if itemsCount}
                <div class="whitespace-nowrap">{itemsCount} Items</div>
              {/if}
              {#if openingsCount}
                <div class="whitespace-nowrap">{openingsCount} Openings</div>
              {/if}
              {#if typesCount}
                <div class="whitespace-nowrap">{typesCount} Types</div>
              {/if}
            </div>
          </div>
          <div class="flex flex-col items-center w-16 gap-2">
            <FileIcon />
            <div>CAD</div>
            <input type="checkbox" bind:checked={includeCAD} />
            <div class="text-gray-600 italic">
              {#if itemsCount}
                <div class="whitespace-nowrap">{itemsCount} Items</div>
              {/if}
              {#if openingsCount}
                <div class="whitespace-nowrap">{openingsCount} Openings</div>
              {/if}
              {#if typesCount}
                <div>&nbsp;</div>
              {/if}
            </div>
          </div>
          {#if showOpenings}
            <div class="flex flex-col items-center w-16 gap-2">
              <WinBidProIcon />
              <div>WinBidPro</div>
              <input type="checkbox" bind:checked={includeWBP} />
              <div class="text-gray-600 italic">
                {#if openingsCount}
                  <div class="whitespace-nowrap">{openingsCount} Openings</div>
                {/if}
                {#if itemsCount}
                  <div>&nbsp;</div>
                {/if}
                {#if typesCount}
                  <div>&nbsp;</div>
                {/if}
              </div>
            </div>
            <div class="flex flex-col items-center w-16 gap-2">
              <GSIcon />
              <div>Glazier&nbsp;Studio</div>
              <input type="checkbox" bind:checked={includeGS} />
              <div class="text-gray-600 italic">
                {#if openingsCount}
                  <div class="whitespace-nowrap">{openingsCount} Openings</div>
                {/if}
                {#if itemsCount}
                  <div>&nbsp;</div>
                {/if}
                {#if typesCount}
                  <div>&nbsp;</div>
                {/if}
              </div>
            </div>
          {/if}
        </div>
        {#if includeGS && !$profile?.confirmed_gs_update_on}
          <ConfirmGsUpdate />
        {/if}
      {/if}
    {/key}
  </div>
</Modal>
